import { objectSpread2 as _objectSpread2, extends as _extends } from '../../../../_virtual/_rollupPluginBabelHelpers.esm.js';
import { filterDOMProps, mergeProps } from '@react-aria/utils';
import React, { useMemo, useRef, useEffect } from 'react';
import { useLocale, useCollator, ListKeyboardDelegate, useListBox, useFocusRing, FocusScope } from 'react-aria';
import { Provider, ListBoxContext, ListStateContext, DragAndDropContext, SeparatorContext, DropIndicatorContext, Separator } from 'react-aria-components';
import { ListBoxDropIndicatorWrapper } from './list-box-drop-indicator.esm.js';
import ListBoxSection from './list-box-section.esm.js';
import Option from './option.esm.js';
import { useRenderProps } from '../../../utils/index.esm.js';
import { useCachedChildren } from '../collection.esm.js';

function ListBoxInner(_ref) {
  var _droppableCollection;
  var state = _ref.state,
    props = _ref.props,
    listBoxRef = _ref.listBoxRef;
  var dragAndDropHooks = props.dragAndDropHooks,
    _props$layout = props.layout,
    layout = _props$layout === void 0 ? 'stack' : _props$layout,
    _props$orientation = props.orientation,
    orientation = _props$orientation === void 0 ? 'vertical' : _props$orientation;
  var collection = state.collection,
    selectionManager = state.selectionManager;
  var isListDraggable = !!(dragAndDropHooks !== null && dragAndDropHooks !== void 0 && dragAndDropHooks.useDraggableCollectionState);
  var isListDroppable = !!(dragAndDropHooks !== null && dragAndDropHooks !== void 0 && dragAndDropHooks.useDroppableCollectionState);
  var _useLocale = useLocale(),
    direction = _useLocale.direction;
  var disabledBehavior = selectionManager.disabledBehavior,
    disabledKeys = selectionManager.disabledKeys;
  var collator = useCollator({
    usage: 'search',
    sensitivity: 'base'
  });
  var keyboardDelegate = useMemo(function () {
    return props.keyboardDelegate || new ListKeyboardDelegate({
      collection: collection,
      collator: collator,
      ref: listBoxRef,
      disabledKeys: disabledKeys,
      disabledBehavior: disabledBehavior,
      layout: layout,
      orientation: orientation,
      direction: direction
    });
  }, [collection, collator, listBoxRef, disabledBehavior, disabledKeys, orientation, direction, props.keyboardDelegate, layout]);
  var _useListBox = useListBox(_objectSpread2(_objectSpread2({}, props), {}, {
      shouldSelectOnPressUp: isListDraggable || props.shouldSelectOnPressUp,
      keyboardDelegate: keyboardDelegate
    }), state, listBoxRef),
    listBoxProps = _useListBox.listBoxProps;
  var children = useCachedChildren({
    items: collection,
    children: function children(item) {
      switch (item.type) {
        case 'section':
          return /*#__PURE__*/React.createElement(ListBoxSection, {
            section: item
          });
        case 'separator':
          return /*#__PURE__*/React.createElement(Separator, item.props);
        case 'item':
          return /*#__PURE__*/React.createElement(Option, {
            item: item
          });
        default:
          throw new Error('Unsupported node type in Menu: ' + item.type);
      }
    }
  });
  var dragHooksProvided = useRef(isListDraggable);
  var dropHooksProvided = useRef(isListDroppable);
  useEffect(function () {
    if (dragHooksProvided.current !== isListDraggable) {
      // eslint-disable-next-line no-console
      console.warn('Drag hooks were provided during one render, but not another. This should be avoided as it may produce unexpected behavior.');
    }
    if (dropHooksProvided.current !== isListDroppable) {
      // eslint-disable-next-line no-console
      console.warn('Drop hooks were provided during one render, but not another. This should be avoided as it may produce unexpected behavior.');
    }
  }, [isListDraggable, isListDroppable]);
  var dragState = undefined;
  var dropState = undefined;
  var droppableCollection = undefined;
  var isRootDropTarget = false;
  var dragPreview = null;
  var preview = useRef(null);
  if (isListDraggable && dragAndDropHooks) {
    dragState = dragAndDropHooks.useDraggableCollectionState({
      collection: collection,
      selectionManager: selectionManager,
      preview: dragAndDropHooks.renderDragPreview ? preview : undefined
    });
    dragAndDropHooks.useDraggableCollection({}, dragState, listBoxRef);
    var DragPreview = dragAndDropHooks.DragPreview;
    dragPreview = dragAndDropHooks.renderDragPreview ? /*#__PURE__*/React.createElement(DragPreview, {
      ref: preview
    }, dragAndDropHooks.renderDragPreview) : null;
  }
  if (isListDroppable && dragAndDropHooks) {
    dropState = dragAndDropHooks.useDroppableCollectionState({
      collection: collection,
      selectionManager: selectionManager
    });
    var dropTargetDelegate = dragAndDropHooks.dropTargetDelegate || new dragAndDropHooks.ListDropTargetDelegate(collection, listBoxRef, {
      orientation: orientation,
      layout: layout,
      direction: direction
    });
    droppableCollection = dragAndDropHooks.useDroppableCollection({
      keyboardDelegate: keyboardDelegate,
      dropTargetDelegate: dropTargetDelegate
    }, dropState, listBoxRef);
    isRootDropTarget = dropState.isDropTarget({
      type: 'root'
    });
  }
  var _useFocusRing = useFocusRing(),
    focusProps = _useFocusRing.focusProps,
    isFocused = _useFocusRing.isFocused,
    isFocusVisible = _useFocusRing.isFocusVisible;
  var renderValues = {
    isDropTarget: isRootDropTarget,
    isEmpty: state.collection.size === 0,
    isFocused: isFocused,
    isFocusVisible: isFocusVisible,
    layout: props.layout || 'stack',
    state: state
  };
  var renderProps = useRenderProps({
    className: props.className,
    style: props.style,
    defaultClassName: 'react-aria-ListBox',
    values: renderValues
  });
  var emptyState = null;
  if (state.collection.size === 0 && props.renderEmptyState) {
    emptyState = /*#__PURE__*/React.createElement("div", {
      role: "option",
      className: "contents"
    }, props.renderEmptyState(renderValues));
  }
  return /*#__PURE__*/React.createElement(FocusScope, null, /*#__PURE__*/React.createElement("div", _extends({}, filterDOMProps(props), mergeProps(listBoxProps, focusProps, (_droppableCollection = droppableCollection) === null || _droppableCollection === void 0 ? void 0 : _droppableCollection.collectionProps), renderProps, {
    ref: listBoxRef,
    slot: props.slot || undefined,
    onScroll: props.onScroll,
    "data-drop-target": isRootDropTarget || undefined,
    "data-empty": state.collection.size === 0 || undefined,
    "data-focused": isFocused || undefined,
    "data-focus-visible": isFocusVisible || undefined,
    "data-layout": props.layout || 'stack',
    "data-orientation": props.orientation || 'vertical'
  }), /*#__PURE__*/React.createElement(Provider
  // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop
  , {
    values: [[ListBoxContext, props], [ListStateContext, state], [DragAndDropContext, {
      dragAndDropHooks: dragAndDropHooks,
      dragState: dragState,
      dropState: dropState
    }], [SeparatorContext, {
      elementType: 'div'
    }], [DropIndicatorContext, {
      render: ListBoxDropIndicatorWrapper
    }]]
  }, children), emptyState, dragPreview));
}

export { ListBoxInner as default };
//# sourceMappingURL=list-box-inner.esm.js.map
